/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.nextUrl = this.dataset.nextUrl || '';
      this.button = this.querySelector('.load-more');
      this.grid = this.getGrid();
      this.animationsEnabled =
        document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.handleButtonClick = this.handleButtonClick.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
    }

    connectedCallback() {
      this.refreshReferences();
      this.toggleButtonVisibility(Boolean(this.nextUrl));

      if (!this.grid || !this.nextUrl) {
        this.stopPagination();
        return;
      }

      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }

      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
    }

    disconnectedCallback() {
      if (this.button) {
        this.button.removeEventListener('click', this.handleButtonClick);
      }

      this.disconnectObserver();
    }

    getGrid() {
      if (!this.sectionId) {
        return document.getElementById('product-grid');
      }

      const sectionId =
        window.CSS && typeof window.CSS.escape === 'function'
          ? window.CSS.escape(this.sectionId)
          : this.sectionId.replace(/"/g, '\\"');

      return (
        document.querySelector(`#product-grid[data-id="${sectionId}"]`) ||
        document.getElementById('product-grid')
      );
    }

    refreshReferences() {
      this.button = this.querySelector('.load-more');
      this.grid = this.getGrid();
      this.nextUrl = this.getNextUrlFromContainer(this);
      this.dataset.nextUrl = this.nextUrl;
    }

    loadMore() {
      if (!this.button || this.button.dataset.paginationBound === 'true') {
        return;
      }

      this.button.dataset.paginationBound = 'true';
      this.button.addEventListener('click', this.handleButtonClick);
    }

    infinite() {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      const target = this.getObserverTarget();
      if (!target) {
        return;
      }

      if (this.observer && this.observedElement === target) {
        return;
      }

      this.disconnectObserver();

      // Preload before the user reaches the actual grid end/footer area.
      this.observedElement = target;
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: this.getObserverRootMargin(),
        threshold: 0
      });
      this.observer.observe(target);
    }

    disconnectObserver() {
      if (!this.observer) {
        this.observedElement = null;
        return;
      }

      this.observer.disconnect();
      this.observer = null;
      this.observedElement = null;
    }

    handleButtonClick(event) {
      event.preventDefault();
      this.loadProducts();
      event.currentTarget.blur();
    }

    handleIntersection(entries) {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.loadProducts();
      }
    }

    getNextUrlFromContainer(container) {
      if (!container) {
        return '';
      }

      if (container.dataset && container.dataset.nextUrl) {
        return container.dataset.nextUrl;
      }

      const nextLink = container.querySelector('.next a:not([disabled])');
      return nextLink ? nextLink.getAttribute('href') || '' : '';
    }

    getGridItemsFromDocument(documentNode) {
      const gridToReplace = documentNode.getElementById('product-grid');
      return gridToReplace ? Array.from(gridToReplace.children) : [];
    }

    getObserverTarget() {
      if (this.grid && this.grid.lastElementChild) {
        return this.grid.lastElementChild;
      }

      return this;
    }

    getObserverRootMargin() {
      const preloadDistance = Math.max(Math.round(window.innerHeight * 0.75), 400);
      return `0px 0px ${preloadDistance}px 0px`;
    }

    setLoadingState(isLoading) {
      if (isLoading) {
        this.setAttribute('loading', 'true');
        return;
      }

      this.removeAttribute('loading');
    }

    toggleButtonVisibility(shouldShow) {
      if (!this.button) {
        return;
      }

      this.button.classList.toggle('visually-hidden', !shouldShow);
    }

    stopPagination() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      this.toggleButtonVisibility(false);
      this.disconnectObserver();
    }

    animateItems(items) {
      if (!this.animationsEnabled || items.length === 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        gsap.set(items, {
          opacity: 0,
          y: 30
        });

        gsap.to(items, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: resolve
        });
      });
    }

    getAddToCartElements(items) {
      return items.reduce((elements, item) => {
        if (item.matches && item.matches('.qlf-add-to-cart:not(.bound)')) {
          elements.push(item);
        }

        if (item.querySelectorAll) {
          elements.push(...item.querySelectorAll('.qlf-add-to-cart:not(.bound)'));
        }

        return elements;
      }, []);
    }

    toggleAddToCartState(element, button, isLoading) {
      const loadingOverlay = element.querySelector('.loading-overlay svg');
      const label = element.querySelector('.single-add-to-cart-button--text');

      button.classList.toggle('loading', isLoading);

      if (loadingOverlay) {
        loadingOverlay.style.opacity = isLoading ? 1 : 0;
      }

      if (label) {
        label.style.display = isLoading ? 'none' : 'block';
      }
    }

    bindAddToCartButtons(items) {
      this.getAddToCartElements(items).forEach((element) => {
        element.classList.add('bound');

        const button = element.querySelector('button');
        if (!button) {
          return;
        }

        button.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();

          if (button.classList.contains('loading')) {
            return;
          }

          const productId = button.dataset.productid;
          if (!productId) {
            return;
          }

          this.toggleAddToCartState(element, button, true);

          try {
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                items: [
                  {
                    id: productId,
                    quantity: 1
                  }
                ]
              })
            });

            if (!response.ok) {
              throw new Error(`Cart add failed with status ${response.status}`);
            }

            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
            document.dispatchEvent(
              new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: response
              })
            );
            document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
          } catch (error) {
            console.error('Error:', error);
          } finally {
            this.toggleAddToCartState(element, button, false);
          }
        });
      });
    }

    async loadProducts() {
      this.refreshReferences();

      if (this.hasAttribute('loading') || !this.grid || !this.nextUrl) {
        return;
      }

      const url = this.nextUrl;
      this.disconnectObserver();
      this.setLoadingState(true);

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Pagination request failed with status ${response.status}`);
        }

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const items = this.getGridItemsFromDocument(parsedDocument);

        this.nextUrl = this.getNextUrlFromContainer(parsedDocument.querySelector('pagination-theme'));
        this.dataset.nextUrl = this.nextUrl;

        if (items.length === 0) {
          this.stopPagination();
          return;
        }

        items.forEach((item) => {
          this.grid.appendChild(item);
        });

        this.bindAddToCartButtons(items);

        document.dispatchEvent(
          new CustomEvent('pagination:page-changed', {
            bubbles: true,
            detail: {
              url: url
            }
          })
        );

        await this.animateItems(items);

        if (!this.nextUrl) {
          this.stopPagination();
        } else if (this.classList.contains('pagination-type--infinite')) {
          this.infinite();
        } else {
          this.toggleButtonVisibility(true);
        }
      } catch (error) {
        console.error('Pagination load failed:', error);
      } finally {
        this.setLoadingState(false);
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}
